import { ComboBox } from '@domgen/base-design-system-2.0/atoms/ComboBox';
import { Input } from '@domgen/base-design-system-2.0/atoms/Input';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { RegBenefits } from '@domgen/base-design-system-2.0/atoms/RegBenefits';
import { Accordion } from '@domgen/base-design-system-2.0/atoms/Accordion';
import { registerBenefits } from './registerBenefits';
import { useEffect, useState } from 'react';
import { useQuery } from '@domgen/base-design-system-2.0/hooks/useQuery';
import { useAppState } from '../../store/selectors/useAppState';
import { useApplianceFormData } from '../../store/selectors/useApplianceFormData';
import { useApplianceLookupResults } from '../../store/selectors/useApplianceLookupResults';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { usePostHog } from '@posthog/react';
import { getSystemCode } from '../../utils/getSystemCode';
import { formApplianceName } from '../../utils/store/formApplianceName';
import { formCategoryName } from '../../utils/store/formCategoryName';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { store } from '../../store/store';
import { ModelNumberLookupResponse } from '../../models/api/queryModelNumber';
import { queryModelNumber } from '../../services/api/queryModelNumber';
import { useFormValidation } from '../../hooks/useFormValidation';
import { FormKey } from '../../enums/formKey';
import { StepKey } from '../../enums/stepKey';
import { formValidators } from '../../utils/form/validators';
import { CircleCheck } from '../../assets/icons/CircleCheck';
import { StoreEvent } from '../../enums/storeEvent';
import { AutoComplete } from '@domgen/shared/enums/autoComplete';

const styles = {
  heroSection: 'relative w-full bg-cover bg-center py-space-700 px-space-500 flex justify-center',
  heroOverlay: 'absolute inset-0 bg-black/30',
  heroContent: 'relative z-10 flex justify-center w-full',
  heroBox:
    'bg-(--color-bg-main-container) rounded-radius-200 p-space-700 flex flex-col gap-space-700 w-3xl shadow-lg',
  heroRow: 'flex flex-col md:flex-row gap-space-700 w-full',
  heroLeft: 'flex flex-col gap-space-400 flex-1',
  heroRight: 'flex flex-col gap-space-400 flex-1',
  canadaLink: 'text-center',
  benefitsSection: 'w-full',
  protectionSection: 'w-full bg-(--color-bg-main) py-space-700 px-space-500',
  protectionContent: 'max-w-6xl mx-auto flex flex-col md:flex-row gap-space-600 items-center',
  protectionText: 'flex flex-col gap-space-400 flex-1',
  protectionImage: 'flex-1 overflow-hidden max-w-137.25',
  faqSection: 'w-full bg-(--color-bg-main-container) py-space-700 px-space-500 pb-space-800',
  faqContent: 'max-w-4xl mx-auto flex flex-col items-center gap-space-500',
  supportBox:
    'flex w-full p-space-400 flex-col items-center rounded-[8px] border border-(--color-card-border-neutral) bg-(--color-bg-main)',
  faqPlaceholder:
    'w-full border border-(--color-card-border-neutral) rounded-radius-200 p-space-500',
};

type LandingPageProps = {
  heroBackground: string;
  brandImage: string;
  brandCode?: string | undefined;
};

export function RegisterPage({ heroBackground, brandImage }: LandingPageProps) {
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const applianceModelResults = useApplianceLookupResults();
  const urlParams = useUrlParams();
  const posthog = usePostHog();
  const [waitForSearchError, setWaitForSearchError] = useState(false);
  const [showSupportBox, setShowSupportBox] = useState(false);
  const brandFriendlyName = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const canadaUrl = 'https://www.hisense-canada.com/en/support/register/';
  const systemCode = getSystemCode(urlParams?.rs);
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();

  useEffect(() => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'olr-landing',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName: applianceName,
      applianceCategory: applianceCategory,
    });
    capturePostHogEvent(posthog, 'landing-page-viewed', store.get());
  }, []);

  const { setQuery, results, loading } = useQuery<ModelNumberLookupResponse>(queryModelNumber, {
    minQueryLength: 3,
  });

  useEffect(() => {
    if (!results?.appliances) return;
    store.send({
      type: StoreEvent.SAVE_APPLIANCE_MODEL_RESULTS,
      data: results.appliances,
    });
  }, [results]);

  const { formValues, errors, handleChange, validateAll, setFormValues } = useFormValidation(
    {
      modelNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
      serialNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] || '',
    },
    {
      modelNumber: [
        formValidators.required('Model number is required'),
        formValidators.alphanumericWithSymbols('Please enter a valid model number'),
        formValidators.minLength(2, 'Model number must be at least 2 characters'),
        formValidators.maxLength(30, 'Model number is too long'),
      ],
      serialNumber: [
        formValidators.optional([
          formValidators.minLength(2, 'Serial number must be at least 2 characters'),
          formValidators.maxLength(20, 'Serial number must be at most 20 characters'),
          formValidators.alphanumericWithSymbols('Please enter a valid serial number'),
        ]),
      ],
    },
  );

  useEffect(() => {
    setFormValues({
      modelNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
      serialNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] || '',
    });
  }, [formData, setFormValues]);

  const handleSupportBoxToggle = () => {
    setShowSupportBox(!showSupportBox);
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'No Model/Serial Number',
      eventLabel: 'No Model/Serial CTA Clicked',
      pagename: 'olr-landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
    });
    capturePostHogEvent(posthog, 'landing-no-model-serial-cta-clicked', store.get());
  };

  const handleContinue = () => {
    if (loading) {
      setWaitForSearchError(true);
      return;
    }

    const isValid = validateAll();

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: !isValid ? 'failed' : 'success',
      pagename: 'olr-landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
    });
    capturePostHogEvent(posthog, 'landing-continue-cta-clicked', store.get());

    if (!isValid) return;

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'serialNumber',
      eventLabel: formValues.serialNumber,
      pagename: 'olr-landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
    });
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'modelNumber',
      eventLabel: formValues.modelNumber,
      pagename: 'olr-landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
    });

    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.MODEL_SERIAL]: {
          [FormKey.MODEL_NUMBER]: formValues.modelNumber,
          [FormKey.SERIAL_NUMBER]: formValues.serialNumber,
        },
      },
    });
  };

  const modelSelectOptions = (results?.appliances || applianceModelResults)?.map((appliance) => ({
    label: appliance.modelNumber,
    value: appliance.modelNumber,
    description: appliance.applianceName,
  }));

  const handleModelSelect = (value: string | null) => {
    setWaitForSearchError(false);
    handleChange({ target: { name: 'modelNumber', value: value ?? '' } });
  };

  const handleModelInputChange = (value: string) => {
    setWaitForSearchError(false);
    setQuery(value);
    handleChange({ target: { name: 'modelNumber', value } });
  };

  const generateOptions = () => {
    if (modelSelectOptions?.some((option) => option.value === formValues.modelNumber)) {
      return modelSelectOptions;
    }
    if (formValues.modelNumber.length < 3 || errors.modelNumber) {
      return [];
    }
    return [
      ...(modelSelectOptions || []),
      { label: formValues.modelNumber, value: formValues.modelNumber },
    ];
  };

  return (
    <div className="flex flex-col items-center gap-space-800">
      <div className={styles.heroSection} style={{ backgroundImage: `url(${heroBackground})` }}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <div className={styles.heroBox}>
            <div className={styles.heroRow}>
              <div className={styles.heroLeft}>
                <h1 className="text-h1">Register your product in a few simple steps</h1>
                <p className="text-body-large">
                  Enter your model and serial number for a quick registration
                </p>
              </div>

              <div className={styles.heroRight}>
                <ComboBox
                  label="Model number"
                  name="modelNumber"
                  placeholder="Search for a model number"
                  onInputChange={handleModelInputChange}
                  options={generateOptions()}
                  defaultValue={formValues.modelNumber}
                  asyncProps={{ loading }}
                  noResultsText="No appliances found"
                  hintText="For example 1234567ABC"
                  required
                  onSelect={handleModelSelect}
                  hasSuccess={!!formValues.modelNumber && !errors['modelNumber']}
                  hasError={
                    waitForSearchError && loading
                      ? 'Please wait for the search to finish before continuing'
                      : errors['modelNumber']
                  }
                  allowCustomValue
                  autoComplete={AutoComplete.OFF}
                />
                <Input
                  placeholder="Enter a serial number (optional)"
                  value={formValues.serialNumber}
                  label="Serial number (optional)"
                  name="serialNumber"
                  onChange={handleChange}
                  hasSuccess={!!formValues.serialNumber && !errors['serialNumber']}
                  hasError={errors['serialNumber']}
                  rightIcon={
                    formValues.serialNumber && !errors.serialNumber ? <CircleCheck /> : undefined
                  }
                  autoComplete={AutoComplete.OFF}
                />
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-1/2">
                <Button onClick={handleContinue}>Continue</Button>
              </div>
            </div>
            <div className="text-center -mt-2">
              <TextLink onClick={handleSupportBoxToggle}>Missing your model number?</TextLink>
            </div>
            {showSupportBox && (
              <div className={styles.supportBox}>
                <p className="text-body text-center">
                  If you don&apos;t have your model number to hand, call us at{' '}
                  <a href="tel:+18553213007" className="text-body-link-strong">
                    +1-855-321-3007
                  </a>{' '}
                  for support.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {canadaUrl && (
        <div className={styles.canadaLink}>
          <a
            href={canadaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-link-strong"
          >
            I am registering my product in Canada
          </a>
        </div>
      )}
      <div className={styles.benefitsSection}>
        <RegBenefits heading="Why register your product?" benefits={registerBenefits} />
      </div>
      <div className={styles.protectionSection}>
        <div className={styles.protectionContent}>
          <div className={styles.protectionText}>
            <h2 className="text-h2">Stay covered for longer with Hisense Care</h2>
            <p className="text-body">
              Protect your product against breakdowns after your warranty ends. Selected plans also
              include accidental damage—from day one. Add Hisense Care when you register.
            </p>
          </div>

          <div className={styles.protectionImage}>
            <img
              src={brandImage}
              alt={`${brandFriendlyName} kitchen`}
              className="w-full h-auto object-cover"
              width={549}
              height={366}
            />
          </div>
        </div>
      </div>

      <div className={styles.faqSection}>
        <div className={styles.faqContent}>
          <h2 className="text-h2 text-center">Your questions</h2>
          <p className="text-body text-center">We're always here to help.</p>
          <Accordion title="What do I need to register my product?">
            You&apos;ll need the model and serial numbers, the approximate purchase price, and the
            delivery date.
          </Accordion>
          <Accordion title="Where can I find my model and serial numbers?">
            You can locate your model and serial numbers on the product, within product
            documentation, or within information provided after your purchase.
          </Accordion>
        </div>
      </div>
    </div>
  );
}
